import { useState } from 'react';
import { Button } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capgo/capacitor-inappbrowser';
import { useTranslation } from 'react-i18next';
import { useTostify } from '@sprytki-frontend-apps/common';
import { ICCTVUrlQueryParams } from 'api/interfaces/cctv';
import { LocationServiceAPI } from 'api/services/locationServiceAPI';

interface CameraPreviewButtonProps {
	locationId: string;
	queryParams?: ICCTVUrlQueryParams;
	fullWidth?: boolean;
	size?: 'small' | 'medium';
}

export const CameraPreviewButton = ({ locationId, queryParams, fullWidth, size }: CameraPreviewButtonProps) => {
	const { t } = useTranslation();
	const toast = useTostify();
	const [isFetching, setIsFetching] = useState(false);

	const showError = (errorCode?: number) =>
		toast(t(`errorCodes.${errorCode}`, { defaultValue: t('pages.alerts.itemDetails.cameraPreviewError') }));

	const openPreview = async () => {
		const isNative = Capacitor.isNativePlatform();
		const previewWindow = isNative ? null : window.open('', '_blank');
		setIsFetching(true);

		try {
			const response = await LocationServiceAPI.getCCTVUrl(locationId, queryParams);

			if (response.success && response.data?.url) {
				const previewUrl = response.data.url.replace(/^http:\/\//i, 'https://');

				if (isNative) {
					await InAppBrowser.openWebView({
						url: previewUrl,
						title: t('pages.alerts.itemDetails.cameraPreview'),
						disableGoBackOnNativeApplication: true,
						showReloadButton: true,
					});
				} else if (previewWindow) {
					previewWindow.location.href = previewUrl;
				} else {
					window.open(previewUrl, '_blank');
				}
				return;
			}

			previewWindow?.close();
			showError(response.errors?.[0]?.errorCode);
		} catch (error: any) {
			previewWindow?.close();
			showError(error?.response?.data?.errors?.[0]?.errorCode);
		} finally {
			setIsFetching(false);
		}
	};

	return (
		<Button
			variant='outlined'
			color='secondary'
			sx={{ borderColor: 'secondary.main' }}
			onClick={openPreview}
			disabled={isFetching}
			fullWidth={fullWidth}
			size={size}
		>
			{t('pages.alerts.itemDetails.cameraPreview')}
		</Button>
	);
};
